import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

// Admin pages live under /admin/* and previously rendered without any
// app chrome — no header, no nav, no way back without the browser back
// button. AdminLayout wraps each admin page in a consistent shell:
// brand on the left, breadcrumb + "Back to app" + sign-out menu on the
// right, sidebar nav listing the admin pages on desktop / top tabs on
// mobile (responsive at 768px).
//
// We deliberately DON'T reuse the main app's UserMenu component here —
// admin context doesn't need Settings / Upgrade-to-Pro / Manage-
// Subscription items, and threading those callbacks across a route
// boundary just to nullify them would be coupling for no benefit. The
// inline AdminUserMenu below is intentionally a stripped-down variant.
//
// Motivation (#56): if/when Gavl pursues county/municipal contracts,
// the admin UI is the product for those customers. Naked admin pages
// with no nav / sign-out / brand affordance look unfinished.

interface AdminLayoutProps {
  pageTitle: string;
  children: React.ReactNode;
}

const ADMIN_NAV: Array<{ label: string; path: string }> = [
  { label: "Feedback", path: "/admin/feedback" },
  { label: "Scraper Health", path: "/admin/scraper-health" },
  { label: "Sharing", path: "/admin/sharing-report" },
  { label: "Data Readiness", path: "/admin/data-readiness" },
  { label: "County Config", path: "/admin/county-config" },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ pageTitle, children }) => {
  const location = useLocation();

  return (
    <div className="admin-layout">
      <header className="admin-layout__header">
        <div className="admin-layout__brand">
          <Link to="/" className="admin-layout__logo" aria-label="Parcely home">
            Parcely
          </Link>
          <span className="admin-layout__breadcrumb" aria-hidden="true">/</span>
          <span className="admin-layout__breadcrumb-section">Admin</span>
        </div>
        <div className="admin-layout__actions">
          <Link to="/" className="admin-layout__back">
            ← Back to app
          </Link>
          <AdminUserMenu />
        </div>
      </header>
      <div className="admin-layout__main">
        <nav className="admin-layout__nav" aria-label="Admin navigation">
          {ADMIN_NAV.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={
                  "admin-layout__nav-link" +
                  (isActive ? " admin-layout__nav-link--active" : "")
                }
                aria-current={isActive ? "page" : undefined}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        <main className="admin-layout__content" aria-labelledby="admin-page-title">
          <h1 id="admin-page-title" className="admin-layout__page-title">{pageTitle}</h1>
          {children}
        </main>
      </div>
    </div>
  );
};

// Stripped-down user menu for admin context. Avatar + email + sign-out
// only; no Settings/Upgrade/Manage-Sub links since those are dashboard
// concerns. See main UserMenu component (`../UserMenu.tsx`) for the
// full version used in the app shell.
const AdminUserMenu: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) return null;

  const email = user.email || "";
  const initial = (email[0] || "?").toUpperCase();

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="user-avatar"
        onClick={() => setOpen(!open)}
        aria-label="Admin user menu"
      >
        {initial}
      </button>
      {open && (
        <div className="user-dropdown">
          <div className="user-dropdown-email">{email}</div>
          <button
            className="user-dropdown-item"
            onClick={() => {
              setOpen(false);
              navigate("/");
            }}
          >
            Back to app
          </button>
          <button
            className="user-dropdown-item user-dropdown-signout"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
